import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';

import type { InstanceStatus } from '@furality/fox-client/models';
import { ToastEnum } from '@furality/furality-purpose';
import type { PartyInviteToastBodyProps } from '@/components/PartyInviteToastBody.tsx';

const MessageTypes = {
  Toast: 'toast',

  EventInstanceCountUpdated: 'event:instance:countUpdated',
  EventInstanceStartStream: 'event:instance:startStream',
  EventInstanceStopStream: 'event:instance:stopStream',
  EventInstanceStatus: 'event:instance:status',

  FriendRequestSent: 'user:vrchat:friendRequestSent',
  UserNotFriendsWithFurality: 'user:vrchat:notFriendsWithFurality',
  UserNotInFuralityConventionGroup: 'user:vrchat:notInFuralityConventionGroup',

  FriendsSynced: 'user:vrchat:friends:synced',
  FriendSyncError: 'user:vrchat:friends:sync:error',

  InstanceInviteSent: 'instance:invite:sent',

  UserRequestJoinParty: 'party:user:joinRequest',
  PartyMemberListChanged: 'party:memberList:changed',
  UserJoinedParty: 'party:user:joined',
  UserLeftParty: 'party:user:left',
  JoinedParty: 'party:joined',

  ReloadApplication: 'frontend:reload',
  ReloadApplicationForce: 'frontend:reload:force',

  DealerBookmarkUpdated: 'dealer:bookmark:updated',
};
type MessageTypes = (typeof MessageTypes)[keyof typeof MessageTypes];

export type InstanceStreamPacket = {
  eventId: string;
  status: 'started' | 'stopped';
};

export type InstanceDataUpdatePacket = {
  instanceName: string;
  playerCount: number | null;
  status: InstanceStatus | null;
};

export type ToastPacket = {
  message: string;
  type: ToastEnum;
  duration?: number;
};

const ToastPacketSchema = Type.Object({
  message: Type.String(),
  type: Type.Union([Type.Literal(ToastEnum.Error), Type.Literal(ToastEnum.Info)]),
  duration: Type.Optional(Type.Number()),
});

export const isToastPacket = (data: unknown): data is ToastPacket => {
  return Value.Check(ToastPacketSchema, data);
};

export type UserRequestJoinPartyPacket = {
  requesterVrchatId: string;
  requesterDisplayName: string;
};

const UserRequestJoinPartyPacketSchema = Type.Object({
  requesterVrchatId: Type.String(),
  requesterDisplayName: Type.String(),
});

export const isUserRequestJoinPartyPacket = (data: unknown): data is UserRequestJoinPartyPacket => {
  return Value.Check(UserRequestJoinPartyPacketSchema, data);
};

export type UserJoinedPartyPacket = {
  userDisplayName: string;
};
export type UserLeftPartyPacket = {
  userDisplayName: string;
};

const UserDisplayNamePacketSchema = Type.Object({
  userDisplayName: Type.String(),
});

export const isUserDisplayNamePacket = (data: unknown): data is { userDisplayName: string } => {
  return Value.Check(UserDisplayNamePacketSchema, data);
};

export type PartyJoinRequestToastPacket = Omit<PartyInviteToastBodyProps, 'id'>;

export default MessageTypes;
